// @ts-strict-ignore
import * as React from 'react';
import { StepRunRouteComponentMatchParams, StepRunComponentProps } from '@/typescriptHelpers';
import { StepChecklistItems } from '../StepChecklistItems';
import Space from 'antd/lib/space';
import Layout from 'antd/lib/layout';
import Spin from 'antd/lib/spin';
import {
    ChecklistItemPlacement,
    IntegrationCapability,
    ReleaseStepType,
    ChecklistItemStatusType,
    ChecklistItem,
    StepRunChecklistItemModel,
    RunwayResponseType,
} from '@/types';
import {
    displayTextForReleaseStepType,
    displayTextForUserGroup,
    displayTextForChecklistItemStatusType,
} from '@/helpers/displayHelpers';
import Divider from 'antd/lib/divider';
import Input from 'antd/lib/input';
import { DestinationCounter } from '@/components/appHome/DestinationCounter';
import Text from 'antd/lib/typography/Text';
import { antGrey, runwayColors } from '@/components/styles/globalStyles';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import {
    selectAppSettings,
    selectAppStoreType,
    selectIntegrationForCapability,
    selectAppHasSelectCapabilitiesConfigured,
    selectJoinedCommsProvidersNamesList,
    selectOrgGroups,
} from '@/selectors/selectors';
import { Link, useParams } from 'react-router-dom';
import Button from 'antd/lib/button';
import { createStepRunRoute } from '@/helpers/routeHelpers';
import { ArrowRightOutlined, CheckCircleOutlined, CheckCircleFilled } from '@ant-design/icons';
import IntegrationIcon from '@/components/icons/IntegrationIcon';
import Tooltip from 'antd/lib/tooltip';
import Popconfirm from 'antd/lib/popconfirm';
import { FilterableKeysAndOptions, FilterTableButton } from '../tableFilters/FilterTableButton';
import { useCallUpdatable, useCallUpdatableForCollections, useError, useGetResponseType } from '@/utils/hooks/helpers';
import { WorkflowRunUpdatable } from '@/actions/workflowRun';
import { globalMessage } from '@/utils/globalMessage';
import { WorkflowRunEntityRequest } from '@/actions/entities';
import { useStepRunsSummary } from '@/utils/hooks/useStepRunsSummary';
import { useInfiniteScroll } from '@/utils/hooks/useInfiniteScroll';
import type { StepRunStatusData } from '@/types/stepRunStatus';

interface StepChecklistItemsContainerProps {
    orgId: string;
    appId: string;
    workflowId: string;
    workflowRunId: string;
    releaseId: string;
    releaseStepType: ReleaseStepType;
    stepRunStatusDataWithChecklistItems: StepRunStatusData[];
    scrollContainerRef: React.RefObject<HTMLDivElement | null>;
}

const StepGroupChecklistContainer: React.FC<StepChecklistItemsContainerProps> = ({
    orgId,
    appId,
    workflowId,
    workflowRunId,
    stepRunStatusDataWithChecklistItems,
    releaseId,
    releaseStepType,
    scrollContainerRef,
}: StepChecklistItemsContainerProps) => {
    const allStepRunIdsWithChecklistItems = React.useMemo(
        () =>
            stepRunStatusDataWithChecklistItems
                .map((statusData) => statusData.stepRunId)
                .filter((id): id is string => id !== null),
        [stepRunStatusDataWithChecklistItems],
    );

    // TODO-WF: Re-enable once we have endpoints on the BE to handle multiples records.
    const showSearchAndFilters = allStepRunIdsWithChecklistItems.length <= 10;
    const { stepRuns, isLoading, hasMore, fetchMore, isFetching } = useStepRunsSummary({
        orgId,
        workflowId,
        workflowRunId,
        stepRunIds: allStepRunIdsWithChecklistItems,
    });

    const sentinelRef = useInfiniteScroll(hasMore, isFetching, fetchMore, scrollContainerRef);

    const platform = useSelector((state: RootState) => selectAppSettings(state, appId).platform);
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const commsIntegration = useSelector((state: RootState) =>
        selectIntegrationForCapability(state, appId, IntegrationCapability.Notifications),
    );
    const hasCommsIntegrationConnected = useSelector((state: RootState) =>
        selectAppHasSelectCapabilitiesConfigured(state, appId, [IntegrationCapability.Notifications]),
    );
    const commsProviders = useSelector(selectJoinedCommsProvidersNamesList);
    const params = useParams<StepRunRouteComponentMatchParams>();
    const stepGroupId = allStepRunIdsWithChecklistItems.join(',');
    const workflowRunDataByStepRunId = React.useMemo(
        () =>
            allStepRunIdsWithChecklistItems.reduce<Record<string, WorkflowRunEntityRequest>>((acc, stepRunId) => {
                acc[stepRunId] = {
                    orgId,
                    workflowId,
                    workflowRunId,
                };
                return acc;
            }, {}),
        [orgId, workflowId, workflowRunId, allStepRunIdsWithChecklistItems],
    );

    const [pingPending, isPingPendingLoading] = useCallUpdatable(
        WorkflowRunUpdatable.PingPendingChecklistItemsForStepGroup,
        {
            orgId,
            workflowId,
            workflowRunId,
        },
        stepGroupId,
    );
    const [pingPendingByStepRunId, isPingPendingLoadingByStepRunId] = useCallUpdatableForCollections(
        WorkflowRunUpdatable.PingPendingChecklistItemsForStepGroup,
        workflowRunDataByStepRunId,
        allStepRunIdsWithChecklistItems,
    );

    const [lastPingPendingStepRunId, setLastPingPendingStepRunId] = React.useState<string>();
    const pingPendingResponseType = useGetResponseType(
        WorkflowRunUpdatable.PingPendingChecklistItemsForStepGroup,
        lastPingPendingStepRunId,
    );
    const pingPendingError = useError(
        WorkflowRunUpdatable.PingPendingChecklistItemsForStepGroup,
        lastPingPendingStepRunId,
    );

    React.useEffect(() => {
        if (pingPendingResponseType === RunwayResponseType.Success) {
            globalMessage.success('Pinged pending cli owners');
        }
    }, [pingPendingResponseType]);

    React.useEffect(() => {
        if (pingPendingError) {
            globalMessage.error(pingPendingError);
        }
    }, [pingPendingError]);

    const onClickPingPending = (targetStepRunIds: string[], targetStepArchetype: ReleaseStepType) => {
        if (targetStepRunIds.length === 1) {
            const stepRunId = targetStepRunIds[0];
            setLastPingPendingStepRunId(stepRunId);
            pingPendingByStepRunId[stepRunId]?.({
                stepRunIds: targetStepRunIds,
                stepArchetype: targetStepArchetype,
            });
            return;
        }
        setLastPingPendingStepRunId(stepGroupId);
        pingPending({
            stepRunIds: targetStepRunIds,
            stepArchetype: targetStepArchetype,
        });
    };

    const renderPingPendingButton = (stepRunIds: string[], stepArchetype: ReleaseStepType) => {
        const loading = stepRunIds.length === 1 ? isPingPendingLoadingByStepRunId[stepRunIds[0]] : isPingPendingLoading;
        const buttonTitle = stepRunIds.length === 1 ? 'Ping pending' : 'Ping all pending';
        const button = (
            <Button
                loading={loading}
                style={{ gap: 0, marginRight: 15 }}
                icon={<IntegrationIcon integrationId={commsIntegration?.id} />}
            >
                <span style={{ paddingLeft: 4 }}>{buttonTitle}</span>
            </Button>
        );

        const buttonWithConfirmation =
            stepRunIds.length > 1 ?
                <Popconfirm
                    title="Are you sure you want to ping all owners of pending items on the step group?"
                    onConfirm={() => onClickPingPending(stepRunIds, stepArchetype)}
                    overlayStyle={{ maxWidth: 360 }}
                >
                    {button}
                </Popconfirm>
            :   <Button
                    onClick={() => onClickPingPending(stepRunIds, stepArchetype)}
                    loading={loading}
                    style={{ gap: 0, marginRight: 15 }}
                    icon={<IntegrationIcon integrationId={commsIntegration?.id} />}
                >
                    <span style={{ paddingLeft: 4 }}>{buttonTitle}</span>
                </Button>;

        if (!hasCommsIntegrationConnected) {
            return (
                <Tooltip title={`Connect ${commsProviders} to ping owners about incomplete items`}>
                    {buttonWithConfirmation}
                </Tooltip>
            );
        }

        return buttonWithConfirmation;
    };

    const [markAsCompleted, isMarkAsCompletedLoading] = useCallUpdatable(
        WorkflowRunUpdatable.BulkMarkCLIsAsCompleted,
        {
            orgId,
            workflowId,
            workflowRunId,
        },
        stepGroupId,
    );
    const [markAsCompletedByStepRunId, isMarkAsCompletedLoadingByStepRunId] = useCallUpdatableForCollections(
        WorkflowRunUpdatable.BulkMarkCLIsAsCompleted,
        workflowRunDataByStepRunId,
        allStepRunIdsWithChecklistItems,
    );

    const [lastMarkAsCompletedTargetId, setLastMarkAsCompletedTargetId] = React.useState<string>();
    const markAsCompletedResponseType = useGetResponseType(
        WorkflowRunUpdatable.BulkMarkCLIsAsCompleted,
        lastMarkAsCompletedTargetId,
    );
    const markAsCompletedError = useError(WorkflowRunUpdatable.BulkMarkCLIsAsCompleted, lastMarkAsCompletedTargetId);

    React.useEffect(() => {
        if (markAsCompletedResponseType === RunwayResponseType.Success) {
            globalMessage.success('Checklist items marked as completed');
        }
    }, [markAsCompletedResponseType]);

    React.useEffect(() => {
        if (markAsCompletedError) {
            globalMessage.error(markAsCompletedError);
        }
    }, [markAsCompletedError]);

    const onClickMarkAsCompleted = (targetStepRunIds: string[]) => {
        if (targetStepRunIds.length === 1) {
            const stepRunId = targetStepRunIds[0];
            setLastMarkAsCompletedTargetId(stepRunId);
            markAsCompletedByStepRunId[stepRunId]?.({
                stepRunIds: targetStepRunIds,
            });
            return;
        }
        setLastMarkAsCompletedTargetId(stepGroupId);
        markAsCompleted({
            stepRunIds: targetStepRunIds,
        });
    };

    const renderMarkAllAsCompletedButton = (stepRunIds: string[]) => {
        const loading =
            stepRunIds.length === 1 ? isMarkAsCompletedLoadingByStepRunId[stepRunIds[0]] : isMarkAsCompletedLoading;
        const buttonTitle = stepRunIds.length === 1 ? 'Mark as completed' : 'Mark all as completed';
        return stepRunIds.length > 1 ?
                <Popconfirm
                    title="Are you sure you want to mark all items on the step group as completed?"
                    onConfirm={() => onClickMarkAsCompleted(stepRunIds)}
                    overlayStyle={{ maxWidth: 360 }}
                >
                    <Button
                        loading={loading}
                        style={{ gap: 0, marginRight: 5 }}
                        icon={<CheckCircleOutlined style={{ color: runwayColors.primary, marginRight: 5 }} />}
                    >
                        {buttonTitle}
                    </Button>
                </Popconfirm>
            :   <Button
                    onClick={() => onClickMarkAsCompleted(stepRunIds)}
                    loading={loading}
                    style={{ gap: 0, marginRight: 5 }}
                    icon={<CheckCircleOutlined style={{ color: runwayColors.primary, marginRight: 5 }} />}
                >
                    {buttonTitle}
                </Button>;
    };

    const flatChecklistItems = React.useMemo<ChecklistItem[]>(() => {
        return stepRuns.reduce<ChecklistItem[]>((acc, stepRun) => {
            return acc.concat(stepRun.checklistItems ?? []);
        }, []);
    }, [stepRuns]);

    const [searchQuery, setSearchQuery] = React.useState('');
    const [filteredItems, setFilteredItems] = React.useState<StepRunChecklistItemModel[]>([]);

    const userGroups = useSelector((state: RootState) => selectOrgGroups(state, orgId));

    const displayNameForGroupId = React.useMemo(() => {
        return Object.values(userGroups).reduce(
            (acc, group) => ({
                ...acc,
                [group.id]: displayTextForUserGroup(group),
            }),
            {},
        );
    }, [userGroups]);

    const searchFilteredItems = React.useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) {
            return flatChecklistItems;
        }

        return flatChecklistItems.filter((item) => {
            const searchableValues = [
                item.title,
                item.description,
                item.status?.status ? displayTextForChecklistItemStatusType(item.status.status) : '',
                ...(item.owners ?? []),
                ...(item.approverGroups ?? []).map((groupId) => displayNameForGroupId[groupId]),
            ];

            return searchableValues.some((value) => value?.toLowerCase().includes(query));
        });
    }, [flatChecklistItems, displayNameForGroupId, searchQuery]);

    const filterableKeysAndOptions = React.useMemo(() => {
        const options: FilterableKeysAndOptions<ChecklistItem> = {
            owners: {
                displayName: 'Owners',
                displayNameForPossibleValue: (value) => {
                    return value;
                },
            },
            approverGroups: {
                displayName: 'Approver user groups',
                displayNameForPossibleValue: (value) => {
                    return displayNameForGroupId[value];
                },
                defaultValues: Object.keys(displayNameForGroupId),
                showAsDropdown: true,
            },
            status: {
                displayName: 'Status',
                displayNameForPossibleValue: (value) => {
                    return displayTextForChecklistItemStatusType(value as ChecklistItemStatusType);
                },
                defaultValues: Object.values(ChecklistItemStatusType),
                extractPossibleValueOrValuesFromItem: (item) => {
                    return item.status?.status ?? 'Not started';
                },
                showAsDropdown: true,
            },
        };
        return options;
    }, [displayNameForGroupId]);

    const updateFilteredItems = React.useCallback(
        (items: ChecklistItem[]) => {
            const checklistItemsByStepRunId = items.reduce<Record<string, ChecklistItem[]>>((list, item) => {
                const stepRunId = item.stepRunId ?? '';
                if (!list[stepRunId]) {
                    list[stepRunId] = [];
                }
                list[stepRunId].push(item);
                return list;
            }, {});

            setFilteredItems(
                stepRuns.map((stepRun) => ({
                    stepRun,
                    filteredChecklistItems: checklistItemsByStepRunId[stepRun.id] ?? [],
                })),
            );
        },
        [stepRuns],
    );

    React.useEffect(() => {
        const initialChecklistItems = stepRuns.map((stepRun) => ({
            stepRun,
            filteredChecklistItems: stepRun.checklistItems,
        }));

        setFilteredItems(initialChecklistItems);
    }, [stepRuns]);

    return (
        <Layout style={{ paddingBottom: 95 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {showSearchAndFilters ?
                    <Input.Search
                        placeholder="Search"
                        style={{ maxWidth: 280 }}
                        allowClear
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                    />
                :   <div />}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {showSearchAndFilters && (
                        <FilterTableButton<ChecklistItem>
                            id={ChecklistItemPlacement.StepChecklist}
                            items={searchFilteredItems}
                            filterableKeysAndOptions={filterableKeysAndOptions}
                            onChange={updateFilteredItems}
                        />
                    )}
                    <span style={{ marginLeft: 15 }}>
                        {renderPingPendingButton(allStepRunIdsWithChecklistItems, releaseStepType)}
                    </span>
                    {renderMarkAllAsCompletedButton(allStepRunIdsWithChecklistItems)}
                </div>
            </div>
            <Divider dashed style={{ marginBottom: 25 }} />
            <div>
                {Array.from(filteredItems.entries()).map(([_, item]) => {
                    const completedCount = item.filteredChecklistItems.filter(
                        (item) => item.status?.status === ChecklistItemStatusType.Approved,
                    ).length;
                    const totalCount = item.filteredChecklistItems.length;
                    const allCompleted = completedCount === totalCount;
                    const completedItemsString = `${completedCount} of ${totalCount}`;
                    const stepRunComponentProps: StepRunComponentProps = {
                        stepRunId: item.stepRun.id,
                        stepConfigId: item.stepRun.stepId,
                        workflowId: workflowId,
                        workflowRunId: workflowRunId,
                        orgId: orgId,
                        releaseId: releaseId,
                        appId: appId,
                    };
                    return (
                        <div key={item.stepRun.id} style={{ marginBottom: 40 }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Space>
                                    {item.stepRun.destinations?.map((destination, idx) => (
                                        <DestinationCounter
                                            key={`${item.stepRun.id}-${destination.name}-${idx}`}
                                            destinations={[destination]}
                                        />
                                    ))}
                                </Space>
                                <Divider type="vertical" style={{ height: '32px' }}></Divider>
                                <Text
                                    style={{
                                        fontSize: 15,
                                        fontWeight: 400,
                                        color: antGrey.grey8,
                                    }}
                                >
                                    {displayTextForReleaseStepType(item.stepRun.type, platform, appStoreType, true)}
                                </Text>
                                <Divider type="vertical" style={{ height: '32px' }}></Divider>
                                <Text
                                    style={{
                                        fontSize: 15,
                                        fontWeight: 400,
                                        color: antGrey.grey8,
                                    }}
                                >
                                    {item.stepRun.displayName}
                                </Text>
                                <Divider type="vertical" style={{ height: '32px' }}></Divider>
                                <div>
                                    <Text
                                        style={{
                                            fontSize: 15,
                                            fontWeight: 400,
                                            color: allCompleted ? runwayColors.statusGreen : runwayColors.statusYellow,
                                        }}
                                    >
                                        {completedItemsString}
                                    </Text>

                                    {allCompleted && (
                                        <CheckCircleFilled style={{ color: runwayColors.statusGreen, marginLeft: 4 }} />
                                    )}
                                </div>
                                <div style={{ marginLeft: 'auto' }}>
                                    {renderPingPendingButton([item.stepRun.id], item.stepRun.type)}
                                    {renderMarkAllAsCompletedButton([item.stepRun.id])}
                                    <Divider type="vertical" style={{ height: '32px' }}></Divider>
                                    <Link
                                        to={createStepRunRoute(
                                            params.orgIdParam,
                                            params.appIdParam,
                                            params.releaseIdParam,
                                            item.stepRun.id,
                                        )}
                                        style={{ alignSelf: 'flex-end' }}
                                    >
                                        <Button
                                            type="link"
                                            style={{
                                                fontSize: 13,
                                                fontWeight: 200,
                                                padding: 0,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'inline-flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                    height: '100%',
                                                }}
                                            >
                                                <ArrowRightOutlined />
                                                View in step
                                            </div>
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                            <Divider
                                type="horizontal"
                                style={{ marginTop: 13, marginBottom: 0, borderWidth: 2 }}
                            ></Divider>
                            <StepChecklistItems
                                orgId={orgId}
                                releaseId={releaseId}
                                appId={appId}
                                releaseStepType={releaseStepType}
                                items={item.filteredChecklistItems}
                                isEditing={false}
                                onEditChecklistItem={null}
                                onAddComment={null}
                                placement={ChecklistItemPlacement.StepChecklist}
                                onUpdateOrder={null}
                                stepRunComponentProps={stepRunComponentProps}
                            />
                        </div>
                    );
                })}
                {(isLoading || hasMore) && (
                    <div
                        ref={sentinelRef}
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            padding: '16px 0',
                            minHeight: 1,
                        }}
                    >
                        {(isLoading || isFetching) && <Spin size="small" />}
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default StepGroupChecklistContainer;
